import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Typography
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { getLogo, getNavigationMenuItems, navigateToPathOrSection } from '../../utils/navigationUtils';
import THEME from '../../constants/theme';
import ServiceStatusModal from '../common/ServiceStatusModal';
import { getOverallStatus } from '../../constants/serviceStatus';
import PoweredBySpinnaker from './PoweredBySpinnaker';

const NavigationMenuMobile = ({ 
  textColor = '#ffffff', 
  isHomePage = false,
  pageSectionRefsContainer,
  startPageTransition 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const menuItems = getNavigationMenuItems();
  const logoSrc = getLogo(textColor);

  const handleNavigate = (path) => {
    const currentRefs = pageSectionRefsContainer
      ? pageSectionRefsContainer.current
      : {};
    navigateToPathOrSection(
      null,
      path,
      currentRefs,
      navigate,
      location.pathname,
      startPageTransition
    );
    setIsOpen(false);
  };

  const handleServiceStatusClick = () => {
    setServiceModalOpen(true);
    setIsOpen(false);
  };

  const handleServiceModalClose = () => {
    setServiceModalOpen(false);
  };

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  if (!isMobile) return null;

  return (
    <>
      {/* Mobile App Bar */}
      <AppBar
        position="fixed"
        sx={{
          background: isHomePage 
            ? 'linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.1) 100%)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: isHomePage ? 'none' : '0 2px 20px rgba(0, 0, 0, 0.1)',
          borderBottom: isHomePage ? 'none' : '1px solid rgba(0, 0, 0, 0.1)',
          zIndex: 1200
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px !important' }}>
          {/* Logo */}
          <Box
            onClick={() => handleNavigate('/')}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              height: '40px'
            }}
          >
            <img
              src={logoSrc}
              alt="OctopusSync Logo"
              style={{
                height: '45px',
                width: 'auto',
                maxWidth: '250px'
              }}
            />
          </Box>

          {/* Menu Button */}
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer}
            sx={{
              color: textColor,
              '&:hover': {
                backgroundColor: isHomePage 
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(101, 17, 175, 0.1)'
              }
            }}
          >
            {isOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '320px',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(244,239,250,0.96) 100%)',
            backdropFilter: 'blur(22px)',
            borderLeft: '1px solid rgba(101, 17, 175, 0.16)',
            boxShadow: '0 18px 50px rgba(0,0,0,0.18)'
          }
        }}
      >
        <Box
          sx={{
            padding: '80px 20px 20px 20px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Logo in Drawer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '40px',
              cursor: 'pointer'
            }}
            onClick={() => handleNavigate('/')}
          >
            <img
              src={getLogo('#000000')}
              alt="OctopusSync Logo"
              style={{
                height: '70px',
                width: 'auto',
                maxWidth: '280px'
              }}
            />
          </Box>

          {/* Navigation Items */}
          <Box
            sx={{
              mb: 3,
              p: 2.5,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(101,17,175,0.12) 0%, rgba(61,175,245,0.08) 100%)',
              border: '1px solid rgba(101,17,175,0.15)',
              boxShadow: '0 12px 30px rgba(101,17,175,0.12)'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.3, color: THEME.COLORS.PRIMARY_PURPLE }}>
              Quick access
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6, color: THEME.COLORS.NEUTRAL_GRAY }}>
              Jump to key sections or check live service status.
            </Typography>
          </Box>

          <List sx={{ flexGrow: 1 }}>
            <AnimatePresence>
              {menuItems.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ListItem
                    button={true}
                    onClick={() => item.isServiceStatus ? handleServiceStatusClick() : handleNavigate(item.path)}
                    sx={{
                      borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                      margin: '10px 0',
                      padding: '14px 18px',
                      transition: 'all 0.28s ease',
                      background: !item.isServiceStatus && (location.pathname === item.path || location.pathname.startsWith(item.path + '/'))
                        ? 'linear-gradient(90deg, rgba(101,17,175,0.92) 0%, rgba(101,17,175,0.82) 100%)'
                        : 'rgba(255,255,255,0.6)',
                      border: !item.isServiceStatus && (location.pathname === item.path || location.pathname.startsWith(item.path + '/'))
                        ? '1px solid rgba(101,17,175,0.4)'
                        : '1px solid rgba(101,17,175,0.08)',
                      boxShadow: !item.isServiceStatus && (location.pathname === item.path || location.pathname.startsWith(item.path + '/'))
                        ? '0 12px 30px rgba(101,17,175,0.25)'
                        : '0 10px 24px rgba(0,0,0,0.06)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, rgba(101,17,175,0.08) 0%, rgba(61,175,245,0.06) 100%)',
                        borderColor: 'rgba(101,17,175,0.2)',
                        transform: 'translateX(6px)'
                      },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                          fontSize: '1.05rem',
                          fontWeight: 600,
                          letterSpacing: 0.2,
                          color: !item.isServiceStatus && (location.pathname === item.path || location.pathname.startsWith(item.path + '/'))
                            ? '#ffffff' 
                            : THEME.COLORS.NEUTRAL_BLACK_LIGHT,
                          textAlign: item.isServiceStatus ? 'left' : 'left'
                        }
                      }}
                    />
                    
                    {/* Service Status Icon */}
                    {item.isServiceStatus && (
                      <Box sx={{ 
                        marginLeft: '16px', 
                        padding: '6px',
                        borderRadius: '50%',
                        backgroundColor: getOverallStatus() ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {getOverallStatus() ? (
                          <CheckCircle sx={{ 
                            color: '#4CAF50', 
                            fontSize: '1.3rem',
                            filter: 'drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3))'
                          }} />
                        ) : (
                          <Cancel sx={{ 
                            color: '#F44336', 
                            fontSize: '1.3rem',
                            filter: 'drop-shadow(0 2px 4px rgba(244, 67, 54, 0.3))'
                          }} />
                        )}
                      </Box>
                    )}
                  </ListItem>
                </motion.div>
              ))}
            </AnimatePresence>
          </List>

          {/* Footer in Drawer */}
          <Box
            sx={{
              textAlign: 'center',
              padding: '20px 0 0',
              borderTop: '1px solid rgba(101, 17, 175, 0.1)',
              marginTop: 'auto'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 2,
                px: 1.5,
                py: 1.25,
                borderRadius: '999px',
                background: isHomePage
                  ? 'linear-gradient(135deg, rgba(18, 18, 18, 0.96) 0%, rgba(46, 46, 46, 0.92) 100%)'
                  : 'rgba(255, 255, 255, 0.7)',
                border: isHomePage
                  ? '1px solid rgba(255, 255, 255, 0.08)'
                  : '1px solid rgba(101, 17, 175, 0.12)',
                boxShadow: isHomePage
                  ? '0 12px 24px rgba(0, 0, 0, 0.18)'
                  : '0 10px 22px rgba(101, 17, 175, 0.08)'
              }}
            >
              <PoweredBySpinnaker
                isHomePage={isHomePage}
                compact
                centered
              />
            </Box>

            <Box
              sx={{
                fontSize: '0.8rem',
                color: THEME.COLORS.NEUTRAL_GRAY,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
              }}
            >
              © 2025 OctopusSync
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Service Status Modal */}
      <ServiceStatusModal
        open={serviceModalOpen}
        onClose={handleServiceModalClose}
      />
    </>
  );
};

NavigationMenuMobile.propTypes = {
  textColor: PropTypes.string,
  isHomePage: PropTypes.bool,
  pageSectionRefsContainer: PropTypes.object,
  startPageTransition: PropTypes.func
};

export default NavigationMenuMobile;
